import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { PlusLg } from 'react-bootstrap-icons';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { Pagination } from '../../shared/components/Pagination';
import { ERole } from '../../shared/enums/ERole';
import { handleError } from '../../shared/functions/handleError';
import { usePagination } from '../../shared/hooks/usePagination';
import { ITruck } from '../../shared/interfaces/ITruck';
import { IUser } from '../../shared/interfaces/IUser';
import { RootState } from '../../store/store';
import TruckForm from './components/TruckForm';
import TruckTable from './components/TruckTable';
import { AdminTruckService } from './service';

const schema = yup.object({
  licensePlate: yup
    .string()
    .required('License plate is required')
    .min(2, 'License plate must be at least 2 characters'),

  mileage: yup
    .number()
    .required('Mileage is required')
    .min(0, 'Mileage must be a positive number'),

  nextServiceKm: yup
    .number()
    .required('Next service km is required')
    .min(0, 'Next service km must be a positive number'),

  alarmSystem: yup.string().required('Alarm system status is required'),

  tachograph: yup.string().required('Tachograph status is required'),

  technicalInspection: yup
    .string()
    .required('Technical inspection date is required')
    .typeError('Technical inspection must be a valid date'),

  brandId: yup
    .number()
    .required('Brand is required')
    .min(1, 'Brand ID must be a positive number'),
});

const AdminTruckPage = () => {
  const { t } = useTranslation();
  const { role } = useSelector(
    (state: RootState) => state.auth.userData,
  ) as IUser;
  const isAdmin = role === ERole.ADMIN;
  const [isActionModalOpened, setIsActionModalOpened] = useState(false);

  const {
    register,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ITruck>({
    resolver: yupResolver(schema),
  });

  const {
    pagination,
    setTotalPages,
    setCount,
    changeFilterHandler,
    totalPages,
    page,
    handlePages,
    handleUpdateList,
  } = usePagination({
    perPage: 10,
  });
  const [trucks, setTrucks] = useState<ITruck[]>([]);

  const handleCloseActionModal = () => {
    setIsActionModalOpened(false);
    reset({
      licensePlate: '',
    });
  };
  const handleSubmitActionModal = async (data: ITruck) => {
    const isUpdate = !!data?.id;
    const adjustedData = {
      ...data,
      services: data.services?.filter((service) => !service.id),
    };
    await AdminTruckService[isUpdate ? 'update' : 'create'](adjustedData)
      .then((response) => {
        if (response) {
          handleUpdateList();
          handleCloseActionModal();
          toast.success(
            t(`toast.${isUpdate ? 'successUpdated' : 'successCreated'}`, {
              name: response?.licensePlate,
            }),
          );
        }
      })
      .catch(handleError);
  };
  useEffect(() => {
    AdminTruckService.getAll(pagination)
      .then((response) => {
        setTrucks(response?.data);
        setTotalPages(Math.ceil(response?.count / response?.perPage));
      })
      .catch(handleError);
  }, [pagination, setCount, setTotalPages]);

  return (
    <div>
      <div className="mb-3">
        <div className="w-100 d-flex justify-content-between align-items-center">
          {isAdmin && (
            <Button
              variant={'success'}
              className={'d-flex align-items-center'}
              onClick={() => setIsActionModalOpened(true)}
            >
              <PlusLg className={'me-1'} />
              {t('button.create')}
            </Button>
          )}
          <div className={'d-flex align-items-center'}>
            <Form.Control
              type="text"
              className={'w-100 mx-1'}
              onChange={(e) =>
                changeFilterHandler('licensePlate', e.target.value)
              }
              placeholder={t('general.searchByLicensePlate')}
            />
          </div>
        </div>
      </div>
      <TruckTable
        trucks={trucks}
        isAdmin={isAdmin}
        reset={reset}
        setIsActionModalOpened={setIsActionModalOpened}
        handleUpdateList={handleUpdateList}
      />

      <Pagination
        page={page}
        totalPages={totalPages}
        handlePagination={handlePages}
        changeFilterHandler={changeFilterHandler}
      />
      <Modal
        show={isActionModalOpened}
        onHide={handleCloseActionModal}
        size={'xl'}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t(watch('id') ? 'general.update' : 'general.create')}{' '}
            {t('general.truck')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TruckForm
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseActionModal}>
            {t('button.close')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(handleSubmitActionModal)}
          >
            {t(watch('id') ? 'button.update' : 'button.create')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminTruckPage;
